import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import { 
  BookOpen, Plus, Save, ArrowLeft, Trash2, Edit, Eye, 
  ChevronDown, ChevronRight, ChevronUp, FileText, Video, Link as LinkIcon, 
  Download, Upload, Image, Music, HelpCircle, Settings, Layers,
  Clock, CheckCircle, XCircle, Play, File, ExternalLink, MoreVertical,
  Copy, Archive, Globe, Lock, Sparkles, Brain, ListChecks, GraduationCap,
  FolderOpen, RefreshCcw
} from 'lucide-react';
import { toast } from 'sonner';

// Lesson type icons
const lessonTypeIcons = {
  text: FileText,
  video: Video,
  pdf: File,
  link: ExternalLink,
  file: Download,
  quiz: HelpCircle,
  image: Image,
  audio: Music
};

// Course level options (stored value -> French label)
const LEVEL_OPTIONS = [
  { value: 'beginner', label: 'Débutant' },
  { value: 'intermediate', label: 'Intermédiaire' },
  { value: 'advanced', label: 'Avancé' },
];

const lessonTypeLabels = {
  text: 'Texte',
  video: 'Vidéo',
  pdf: 'PDF',
  link: 'Lien externe',
  file: 'Fichier téléchargeable',
  quiz: 'Quiz',
  image: 'Image',
  audio: 'Audio'
};

// Resolve a stored file URL to an absolute URL pointing at the backend.
// Files are served by the backend StaticFiles mount at /api/uploads.
const resolveFileUrl = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  // Legacy paths were stored without the /api prefix
  let path = url.startsWith('/uploads/content/') ? `/api${url}` : url;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
};

const CourseEditorPage = ({ onMenuClick }) => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  
  // Course data
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Selection state
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  
  // Editor mode
  const [editorMode, setEditorMode] = useState('simple'); // 'simple' or 'advanced'
  
  // Dialogs
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  const [showLessonDialog, setShowLessonDialog] = useState(false);
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCourseSettings, setShowCourseSettings] = useState(false);
  
  // Form data
  const [moduleForm, setModuleForm] = useState({ name: '', description: '' });
  const [lessonForm, setLessonForm] = useState({ 
    name: '', lesson_type: 'text', content: '', video_url: '', 
    document_url: '', external_url: '', duration_minutes: ''
  });
  const [editingModule, setEditingModule] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Quiz state
  const [quizForm, setQuizForm] = useState({
    title: '', description: '', passing_score: 70, duration_minutes: '',
    questions: []
  });
  
  // AI Quiz Generator state
  const [showAIQuizDialog, setShowAIQuizDialog] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  
  // Autosave timer
  const autosaveTimer = useRef(null);
  
  // Fetch course data
  const fetchCourse = useCallback(async () => {
    try {
      const response = await api.get(`/content/courses/${courseId}`);
      setCourse(response.data);
      
      // Expand all modules by default
      const expanded = {};
      response.data.modules?.forEach(m => { expanded[m.id] = true; });
      setExpandedModules(expanded);
      
      // Select first lesson if exists
      if (response.data.modules?.length > 0 && response.data.modules[0].lessons?.length > 0) {
        setSelectedModuleId(response.data.modules[0].id);
        setSelectedLessonId(response.data.modules[0].lessons[0].id);
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Erreur lors du chargement du cours');
    } finally {
      setLoading(false);
    }
  }, [courseId]);
  
  useEffect(() => {
    fetchCourse();
  }, [fetchCourse]);
  
  // Autosave functionality
  const triggerAutosave = useCallback(() => {
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
    }
    autosaveTimer.current = setTimeout(() => {
      setLastSaved(new Date());
    }, 2000);
  }, []);
  
  // Get selected lesson data
  const getSelectedLesson = () => {
    if (!course || !selectedLessonId) return null;
    for (const module of course.modules || []) {
      const lesson = module.lessons?.find(l => l.id === selectedLessonId);
      if (lesson) return { ...lesson, module };
    }
    return null;
  };
  
  const selectedLesson = getSelectedLesson();
  const selectedModule = course?.modules?.find(m => m.id === selectedModuleId);
  
  // ========== MODULE CRUD ==========
  const handleCreateModule = async () => {
    if (!moduleForm.name.trim()) {
      toast.error('Le nom du module est requis');
      return;
    }
    
    try {
      const response = await api.post(`/content/courses/${courseId}/modules`, moduleForm);
      toast.success('Module créé');
      setShowModuleDialog(false);
      setModuleForm({ name: '', description: '' });
      fetchCourse();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };
  
  const handleUpdateModule = async () => {
    if (!editingModule) return;
    
    try {
      await api.put(`/content/courses/${courseId}/modules/${editingModule.id}`, moduleForm);
      toast.success('Module mis à jour');
      setShowModuleDialog(false);
      setEditingModule(null);
      setModuleForm({ name: '', description: '' });
      fetchCourse();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };
  
  const handleDeleteModule = async () => {
    if (!deleteTarget || deleteTarget.type !== 'module') return;
    
    try {
      await api.delete(`/content/courses/${courseId}/modules/${deleteTarget.id}`);
      toast.success('Module supprimé');
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      fetchCourse();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };
  
  // ========== LESSON CRUD ==========
  const buildLessonPayload = () => ({
    ...lessonForm,
    duration_minutes: lessonForm.duration_minutes === '' || lessonForm.duration_minutes === null
      ? null
      : parseInt(lessonForm.duration_minutes, 10)
  });

  const handleCreateLesson = async () => {
    if (!lessonForm.name.trim() || !selectedModuleId) {
      toast.error('Le nom de la leçon est requis');
      return;
    }
    
    try {
      const response = await api.post(`/content/modules/${selectedModuleId}/lessons`, buildLessonPayload());
      toast.success('Leçon créée');
      setShowLessonDialog(false);
      resetLessonForm();
      fetchCourse();
      setSelectedLessonId(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateLesson = async () => {
    if (!editingLesson) return;
    
    try {
      await api.put(`/content/lessons/${editingLesson.id}`, buildLessonPayload());
      toast.success('Leçon mise à jour');
      setShowLessonDialog(false);
      setEditingLesson(null);
      resetLessonForm();
      fetchCourse();
      triggerAutosave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };
  
  const handleDeleteLesson = async () => {
    if (!deleteTarget || deleteTarget.type !== 'lesson') return;
    
    try {
      await api.delete(`/content/lessons/${deleteTarget.id}`);
      toast.success('Leçon supprimée');
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      if (selectedLessonId === deleteTarget.id) {
        setSelectedLessonId(null);
      }
      fetchCourse();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };
  
  const resetLessonForm = () => {
    setLessonForm({ 
      name: '', lesson_type: 'text', content: '', video_url: '', 
      document_url: '', external_url: '', duration_minutes: ''
    });
  };
  
  // ========== LESSON CONTENT UPDATE ==========
  // Pending edits are saved on a debounce so typing stays instant instead of
  // firing (and awaiting) an API request on every keystroke.
  const pendingSave = useRef({});
  const saveTimer = useRef(null);

  const flushLessonSaves = useCallback(async () => {
    const pending = pendingSave.current;
    pendingSave.current = {};
    const entries = Object.entries(pending);
    if (entries.length === 0) return;
    try {
      await Promise.all(
        entries.map(([lessonId, fields]) => api.put(`/content/lessons/${lessonId}`, fields))
      );
      setLastSaved(new Date());
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  }, []);

  const updateLessonContent = (field, value) => {
    if (!selectedLessonId) return;
    const lessonId = selectedLessonId;

    // Update local state immediately so the input stays responsive.
    setCourse(prev => ({
      ...prev,
      modules: prev.modules.map(m => ({
        ...m,
        lessons: m.lessons.map(l =>
          l.id === lessonId ? { ...l, [field]: value } : l
        )
      }))
    }));

    // Queue a debounced persistence of the latest values.
    pendingSave.current[lessonId] = { ...(pendingSave.current[lessonId] || {}), [field]: value };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushLessonSaves, 700);
  };

  // Persist any pending edits when leaving the page.
  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      flushLessonSaves();
    };
  }, [flushLessonSaves]);
  
  // ========== FILE UPLOAD ==========
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedLessonId) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post(`/content/lessons/${selectedLessonId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Fichier uploadé');
      fetchCourse();
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    }
  };
  
  // ========== PUBLISH/UNPUBLISH ==========
  const getPublishBlockers = (c = course) => {
    if (!c) return ['cours non chargé'];
    const blockers = [];
    const hasTrainer = !!(c.has_assigned_trainer || c.trainer_id);
    const hasContent = (c.modules || []).some((m) => (m.lessons || []).length > 0)
      || ((c.module_count || 0) > 0 && (c.lesson_count || 0) > 0);
    if (!hasTrainer) blockers.push('au moins un formateur assigné');
    if (!hasContent) blockers.push('au moins un module contenant une leçon');
    return blockers;
  };

  const canPublishCourse = getPublishBlockers().length === 0;

  const toggleCoursePublish = async () => {
    const nextPublished = !course.is_published;
    if (nextPublished) {
      const blockers = getPublishBlockers();
      if (blockers.length) {
        toast.error(`Impossible de publier. Requis : ${blockers.join(', ')}.`);
        return;
      }
    }
    try {
      await api.put(`/content/courses/${courseId}`, { 
        is_published: nextPublished 
      });
      setCourse(prev => ({ ...prev, is_published: nextPublished }));
      toast.success(nextPublished ? 'Cours publié' : 'Cours dépublié');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };
  
  const updateCourseField = async (field, value) => {
    try {
      await api.put(`/content/courses/${courseId}`, { [field]: value });
      setCourse(prev => ({ ...prev, [field]: value }));
      toast.success('Cours mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleLessonPublish = async (lessonId, currentState) => {
    try {
      await api.put(`/content/lessons/${lessonId}`, { 
        is_published: !currentState 
      });
      fetchCourse();
      toast.success(currentState ? 'Leçon masquée' : 'Leçon publiée');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };
  
  // Open edit dialogs
  const openModuleEdit = (module) => {
    setEditingModule(module);
    setModuleForm({ name: module.name, description: module.description || '' });
    setShowModuleDialog(true);
  };
  
  const openLessonEdit = (lesson) => {
    setEditingLesson(lesson);
    setLessonForm({
      name: lesson.name,
      lesson_type: lesson.lesson_type || 'text',
      content: lesson.content || '',
      video_url: lesson.video_url || '',
      document_url: lesson.document_url || '',
      external_url: lesson.external_url || '',
      duration_minutes: lesson.duration_minutes?.toString() || ''
    });
    setShowLessonDialog(true);
  };
  
  const openDelete = (type, id, name) => {
    setDeleteTarget({ type, id, name });
    setShowDeleteDialog(true);
  };
  
  // ========== AI QUIZ GENERATOR ==========
  const handleGenerateAIQuiz = async () => {
    if (!selectedLessonId) return;
    
    setAiGenerating(true);
    try {
      const response = await api.post(`/content/ai/generate-quiz-from-lesson/${selectedLessonId}?num_questions=5`);
      if (response.data.generated_questions) {
        setGeneratedQuestions(response.data.generated_questions);
        setShowAIQuizDialog(true);
        toast.success(`${response.data.generated_questions.length} questions générées !`);
      } else if (response.data.error) {
        toast.error(response.data.error);
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setAiGenerating(false);
    }
  };
  
  // ========== DRAG AND DROP ==========
  const [draggedItem, setDraggedItem] = useState(null);
  
  const handleDragStart = (e, type, item, moduleId = null) => {
    setDraggedItem({ type, item, moduleId });
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDropModule = async (e, targetIndex) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.type !== 'module') return;
    
    const modules = [...course.modules];
    const draggedIndex = modules.findIndex(m => m.id === draggedItem.item.id);
    
    if (draggedIndex === targetIndex) return;
    
    const [removed] = modules.splice(draggedIndex, 1);
    modules.splice(targetIndex, 0, removed);
    
    // Update order
    const reorderItems = modules.map((m, i) => ({ id: m.id, order: i }));
    
    try {
      await api.post(`/content/courses/${courseId}/modules/reorder`, { items: reorderItems });
      fetchCourse();
    } catch (error) {
      toast.error('Erreur lors du réordonnement');
    }
    
    setDraggedItem(null);
  };
  
  // Move a lesson up or down within its module using the reorder API
  const moveLesson = async (e, module, lessonIndex, direction) => {
    e.stopPropagation();
    const lessons = [...(module.lessons || [])];
    const targetIndex = direction === 'up' ? lessonIndex - 1 : lessonIndex + 1;
    if (targetIndex < 0 || targetIndex >= lessons.length) return;

    const [removed] = lessons.splice(lessonIndex, 1);
    lessons.splice(targetIndex, 0, removed);

    const reorderItems = lessons.map((l, i) => ({ id: l.id, order: i }));

    try {
      await api.post(`/content/modules/${module.id}/lessons/reorder`, { items: reorderItems });
      fetchCourse();
    } catch (error) {
      toast.error('Erreur lors du réordonnement');
    }
  };

  const handleDropLesson = async (e, targetModuleId, targetIndex) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.type !== 'lesson') return;
    
    const targetModule = course.modules.find(m => m.id === targetModuleId);
    if (!targetModule) return;
    
    const lessons = [...targetModule.lessons];
    const draggedIndex = lessons.findIndex(l => l.id === draggedItem.item.id);
    
    if (draggedItem.moduleId === targetModuleId) {
      // Same module reorder
      if (draggedIndex === targetIndex) return;
      const [removed] = lessons.splice(draggedIndex, 1);
      lessons.splice(targetIndex, 0, removed);
    } else {
      // Move to different module - would need additional API
      lessons.splice(targetIndex, 0, draggedItem.item);
    }
    
    const reorderItems = lessons.map((l, i) => ({ id: l.id, order: i }));
    
    try {
      await api.post(`/content/modules/${targetModuleId}/lessons/reorder`, { items: reorderItems });
      fetchCourse();
    } catch (error) {
      toast.error('Erreur lors du réordonnement');
    }
    
    setDraggedItem(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Cours non trouvé</p>
          <Button onClick={() => navigate('/courses')} className="mt-4">
            Retour aux cours
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900">
      {/* Top Bar */}
      <header className="sticky top-0 z-50 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/courses')}
              data-testid="back-to-courses"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-semibold text-lg text-slate-900 dark:text-white line-clamp-1">
                {course.name}
              </h1>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span>{course.module_count || 0} modules</span>
                <span>•</span>
                <span>{course.lesson_count || 0} leçons</span>
                {lastSaved && (
                  <>
                    <span>•</span>
                    <span className="text-emerald-600">
                      Sauvegardé à {lastSaved.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Editor Mode Toggle */}
            <div className="hidden md:flex items-center gap-2 mr-4 bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
              <Button 
                size="sm" 
                variant={editorMode === 'simple' ? 'default' : 'ghost'}
                onClick={() => setEditorMode('simple')}
                className="text-xs"
              >
                Simple
              </Button>
              <Button 
                size="sm" 
                variant={editorMode === 'advanced' ? 'default' : 'ghost'}
                onClick={() => setEditorMode('advanced')}
                className="text-xs"
              >
                Avancé
              </Button>
            </div>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowCourseSettings(true)}
            >
              <Settings className="w-4 h-4 mr-1" />
              Paramètres
            </Button>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open(`/courses/${courseId}`, '_blank')}
            >
              <Eye className="w-4 h-4 mr-1" />
              Aperçu
            </Button>
            
            <Button 
              onClick={toggleCoursePublish}
              disabled={!course.is_published && !canPublishCourse}
              title={
                !course.is_published && !canPublishCourse
                  ? `Requis : ${getPublishBlockers().join(', ')}`
                  : undefined
              }
              className={course.is_published 
                ? 'bg-amber-500 hover:bg-amber-600' 
                : 'bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed'
              }
              data-testid="toggle-course-publish"
            >
              {course.is_published ? (
                <>
                  <Lock className="w-4 h-4 mr-1" />
                  Dépublier
                </>
              ) : (
                <>
                  <Globe className="w-4 h-4 mr-1" />
                  Publier
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content - 3 Column Layout */}
      <div className="flex h-[calc(100vh-65px)]">
        {/* LEFT COLUMN - Course Structure */}
        <div className="w-72 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 flex flex-col">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold text-slate-900 dark:text-white">Structure du cours</h2>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={fetchCourse}
              >
                <RefreshCcw className="w-4 h-4" />
              </Button>
            </div>
            <Button 
              onClick={() => { setEditingModule(null); setModuleForm({ name: '', description: '' }); setShowModuleDialog(true); }}
              className="w-full bg-violet-600 hover:bg-violet-700"
              size="sm"
              data-testid="add-module-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Ajouter un module
            </Button>
          </div>
          
          <ScrollArea className="flex-1">
            <div className="p-2">
              {course.modules?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucun module</p>
                  <p className="text-xs">Créez votre premier module pour commencer</p>
                </div>
              ) : (
                course.modules?.map((module, moduleIndex) => (
                  <div 
                    key={module.id}
                    className="mb-2"
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'module', module)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDropModule(e, moduleIndex)}
                  >
                    {/* Module Header */}
                    <div 
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedModuleId === module.id 
                          ? 'bg-violet-50 dark:bg-violet-900/20' 
                          : 'hover:bg-slate-50 dark:hover:bg-slate-700'
                      }`}
                      onClick={() => {
                        setSelectedModuleId(module.id);
                        setExpandedModules(prev => ({ ...prev, [module.id]: !prev[module.id] }));
                      }}
                    >
                      <button className="p-0.5">
                        {expandedModules[module.id] ? (
                          <ChevronDown className="w-4 h-4 text-slate-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-500" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-900 dark:text-white truncate">
                          {module.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {module.lessons?.length || 0} leçons
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={(e) => { e.stopPropagation(); openModuleEdit(module); }}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 text-red-500 hover:text-red-700"
                          onClick={(e) => { e.stopPropagation(); openDelete('module', module.id, module.name); }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Lessons */}
                    {expandedModules[module.id] && (
                      <div className="ml-6 mt-1 space-y-1">
                        {module.lessons?.map((lesson, lessonIndex) => {
                          const LessonIcon = lessonTypeIcons[lesson.lesson_type] || FileText;
                          return (
                            <div
                              key={lesson.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, 'lesson', lesson, module.id)}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDropLesson(e, module.id, lessonIndex)}
                              className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${
                                selectedLessonId === lesson.id 
                                  ? 'bg-violet-100 dark:bg-violet-800/30 border-l-2 border-violet-500' 
                                  : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                              }`}
                              onClick={() => {
                                setSelectedModuleId(module.id);
                                setSelectedLessonId(lesson.id);
                              }}
                              data-testid={`lesson-item-${lesson.id}`}
                            >
                              <LessonIcon className={`w-4 h-4 ${
                                lesson.is_published ? 'text-emerald-500' : 'text-slate-400'
                              }`} />
                              <span className="flex-1 text-sm truncate text-slate-700 dark:text-slate-300">
                                {lesson.name}
                              </span>
                              {!lesson.is_published && (
                                <Badge variant="outline" className="text-xs h-5">
                                  Brouillon
                                </Badge>
                              )}
                              <div className="flex flex-col">
                                <button
                                  className="p-0.5 text-slate-400 hover:text-violet-600 disabled:opacity-30 disabled:hover:text-slate-400"
                                  disabled={lessonIndex === 0}
                                  onClick={(e) => moveLesson(e, module, lessonIndex, 'up')}
                                  title="Monter"
                                  data-testid={`lesson-move-up-${lesson.id}`}
                                >
                                  <ChevronUp className="w-3 h-3" />
                                </button>
                                <button
                                  className="p-0.5 text-slate-400 hover:text-violet-600 disabled:opacity-30 disabled:hover:text-slate-400"
                                  disabled={lessonIndex === (module.lessons?.length || 0) - 1}
                                  onClick={(e) => moveLesson(e, module, lessonIndex, 'down')}
                                  title="Descendre"
                                  data-testid={`lesson-move-down-${lesson.id}`}
                                >
                                  <ChevronDown className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Add Lesson Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start text-slate-500 hover:text-violet-600"
                          onClick={() => { 
                            setSelectedModuleId(module.id);
                            setEditingLesson(null); 
                            resetLessonForm(); 
                            setShowLessonDialog(true); 
                          }}
                          data-testid={`add-lesson-btn-${module.id}`}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Ajouter une leçon
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* CENTER - Content Editor */}
        <div className="flex-1 overflow-auto">
          {selectedLesson ? (
            <div className="max-w-4xl mx-auto p-6">
              {/* Lesson Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-violet-100 text-violet-700">
                      {lessonTypeLabels[selectedLesson.lesson_type]}
                    </Badge>
                    <Badge className={selectedLesson.is_published 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-slate-100 text-slate-600'
                    }>
                      {selectedLesson.is_published ? 'Publié' : 'Brouillon'}
                    </Badge>
                  </div>
                  <Input
                    value={selectedLesson.name}
                    onChange={(e) => updateLessonContent('name', e.target.value)}
                    className="text-2xl font-bold border-none p-0 h-auto focus-visible:ring-0 bg-transparent"
                    placeholder="Titre de la leçon"
                    data-testid="lesson-title-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openLessonEdit(selectedLesson)}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => toggleLessonPublish(selectedLesson.id, selectedLesson.is_published)}
                  >
                    {selectedLesson.is_published ? 'Masquer' : 'Publier'}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-red-500"
                    onClick={() => openDelete('lesson', selectedLesson.id, selectedLesson.name)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Content Editor based on lesson type */}
              <Card>
                <CardContent className="p-6">
                  {/* TEXT LESSON */}
                  {selectedLesson.lesson_type === 'text' && (
                    <div>
                      <Label className="mb-2 block">Contenu de la leçon</Label>
                      {/* Uncontrolled + keyed by lesson: avoids the controlled-value race that
                          dropped/replaced characters when typing fast. Persisted via debounce. */}
                      <Textarea
                        key={`content-text-${selectedLesson.id}`}
                        defaultValue={selectedLesson.content || ''}
                        onChange={(e) => updateLessonContent('content', e.target.value)}
                        placeholder="Écrivez le contenu de votre leçon ici..."
                        className="min-h-[400px] resize-y"
                        data-testid="lesson-content-textarea"
                      />
                      <p className="text-xs text-slate-500 mt-2">
                        Astuce: Vous pouvez utiliser du Markdown pour formater votre texte
                      </p>
                    </div>
                  )}
                  
                  {/* VIDEO LESSON */}
                  {selectedLesson.lesson_type === 'video' && (
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-2 block">URL de la vidéo (YouTube, Vimeo, ou lien direct)</Label>
                        <Input
                          value={selectedLesson.video_url || ''}
                          onChange={(e) => updateLessonContent('video_url', e.target.value)}
                          placeholder="https://www.youtube.com/watch?v=..."
                          data-testid="lesson-video-url-input"
                        />
                      </div>
                      
                      {selectedLesson.video_url && (
                        <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden">
                          {selectedLesson.video_url.includes('youtube') ? (
                            <iframe
                              src={selectedLesson.video_url.replace('watch?v=', 'embed/')}
                              className="w-full h-full"
                              allowFullScreen
                            />
                          ) : (
                            <video
                              src={resolveFileUrl(selectedLesson.video_url)}
                              controls
                              className="w-full h-full"
                            />
                          )}
                        </div>
                      )}
                      
                      <Separator />
                      
                      <div>
                        <Label className="mb-2 block">Ou uploader un fichier vidéo</Label>
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                          <input
                            type="file"
                            accept="video/*"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="video-upload"
                          />
                          <label htmlFor="video-upload" className="cursor-pointer">
                            <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                            <p className="text-sm text-slate-600">
                              Cliquez pour uploader ou glissez-déposez
                            </p>
                            <p className="text-xs text-slate-400">MP4, WebM, MOV (max 500MB)</p>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* PDF LESSON */}
                  {selectedLesson.lesson_type === 'pdf' && (
                    <div className="space-y-4">
                      {selectedLesson.document_url ? (
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <File className="w-5 h-5 text-red-500" />
                              <span className="font-medium">Document PDF</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="file"
                                accept=".pdf"
                                onChange={handleFileUpload}
                                className="hidden"
                                id="pdf-replace"
                              />
                              <label htmlFor="pdf-replace">
                                <Button variant="outline" size="sm" asChild>
                                  <span className="cursor-pointer">
                                    <Upload className="w-4 h-4 mr-1" />
                                    Remplacer
                                  </span>
                                </Button>
                              </label>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(resolveFileUrl(selectedLesson.document_url), '_blank')}
                              >
                                <ExternalLink className="w-4 h-4 mr-1" />
                                Ouvrir
                              </Button>
                            </div>
                          </div>
                          <iframe
                            src={resolveFileUrl(selectedLesson.document_url)}
                            title={selectedLesson.name}
                            className="w-full h-[600px] rounded-lg border"
                          />
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="pdf-upload"
                          />
                          <label htmlFor="pdf-upload" className="cursor-pointer">
                            <File className="w-8 h-8 mx-auto mb-2 text-red-400" />
                            <p className="text-sm text-slate-600">
                              Cliquez pour uploader un PDF
                            </p>
                            <p className="text-xs text-slate-400">PDF (max 50MB)</p>
                          </label>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* LINK LESSON */}
                  {selectedLesson.lesson_type === 'link' && (
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-2 block">URL externe</Label>
                        <Input
                          value={selectedLesson.external_url || ''}
                          onChange={(e) => updateLessonContent('external_url', e.target.value)}
                          placeholder="https://..."
                        />
                      </div>
                      
                      {selectedLesson.external_url && (
                        <Card className="bg-slate-50">
                          <CardContent className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <ExternalLink className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium">Lien externe</p>
                                <p className="text-sm text-slate-500 truncate max-w-md">
                                  {selectedLesson.external_url}
                                </p>
                              </div>
                            </div>
                            <Button 
                              onClick={() => window.open(selectedLesson.external_url, '_blank')}
                            >
                              Ouvrir
                            </Button>
                          </CardContent>
                        </Card>
                      )}
                      
                      <div>
                        <Label className="mb-2 block">Description (optionnelle)</Label>
                        <Textarea
                          key={`content-link-${selectedLesson.id}`}
                          defaultValue={selectedLesson.content || ''}
                          onChange={(e) => updateLessonContent('content', e.target.value)}
                          placeholder="Décrivez le contenu du lien..."
                          rows={4}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* FILE/DOWNLOAD LESSON */}
                  {selectedLesson.lesson_type === 'file' && (
                    <div className="space-y-4">
                      {selectedLesson.document_url && (
                        <Card className="bg-slate-50">
                          <CardContent className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                                <File className="w-5 h-5 text-emerald-600" />
                              </div>
                              <div>
                                <p className="font-medium">Fichier téléchargé</p>
                                <p className="text-sm text-slate-500 truncate max-w-md">
                                  {selectedLesson.document_url}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              onClick={() => window.open(resolveFileUrl(selectedLesson.document_url), '_blank')}
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Ouvrir
                            </Button>
                          </CardContent>
                        </Card>
                      )}

                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                        <input
                          type="file"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="file-upload"
                        />
                        <label htmlFor="file-upload" className="cursor-pointer">
                          <Download className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                          <p className="text-sm text-slate-600">
                            {selectedLesson.document_url
                              ? 'Cliquez pour remplacer le fichier'
                              : 'Cliquez pour uploader un fichier'}
                          </p>
                          <p className="text-xs text-slate-400">
                            PDF, Word, PowerPoint, ZIP, etc.
                          </p>
                        </label>
                      </div>
                      
                      <div>
                        <Label className="mb-2 block">Instructions de téléchargement</Label>
                        <Textarea
                          key={`content-file-${selectedLesson.id}`}
                          defaultValue={selectedLesson.content || ''}
                          onChange={(e) => updateLessonContent('content', e.target.value)}
                          placeholder="Instructions pour l'étudiant..."
                          rows={4}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* QUIZ LESSON */}
                  {selectedLesson.lesson_type === 'quiz' && (
                    <div className="space-y-4">
                      {selectedLesson.has_quiz ? (
                        <Card className="bg-violet-50 border-violet-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <HelpCircle className="w-6 h-6 text-violet-600" />
                                <div>
                                  <p className="font-medium text-violet-900">Quiz attaché</p>
                                  <p className="text-sm text-violet-600">
                                    Quiz ID: {selectedLesson.quiz_id}
                                  </p>
                                </div>
                              </div>
                              <Button 
                                onClick={() => navigate(`/trainer/create-evaluation?lesson=${selectedLesson.id}&course=${courseId}&module=${selectedLesson.module?.id}`)}
                              >
                                Modifier le quiz
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ) : (
                        <div className="text-center py-8">
                          <HelpCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                          <p className="text-slate-600 mb-4">Aucun quiz attaché à cette leçon</p>
                          <Button 
                            onClick={() => navigate(`/trainer/create-evaluation?lesson=${selectedLesson.id}&course=${courseId}&module=${selectedLesson.module?.id}`)}
                            className="bg-violet-600 hover:bg-violet-700"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Créer un quiz
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* IMAGE LESSON */}
                  {selectedLesson.lesson_type === 'image' && (
                    <div className="space-y-4">
                      {selectedLesson.document_url ? (
                        <img 
                          src={resolveFileUrl(selectedLesson.document_url)} 
                          alt={selectedLesson.name}
                          className="max-w-full rounded-lg"
                        />
                      ) : (
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="image-upload"
                          />
                          <label htmlFor="image-upload" className="cursor-pointer">
                            <Image className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                            <p className="text-sm text-slate-600">
                              Cliquez pour uploader une image
                            </p>
                            <p className="text-xs text-slate-400">PNG, JPG, GIF, WebP</p>
                          </label>
                        </div>
                      )}
                      
                      <div>
                        <Label className="mb-2 block">Légende</Label>
                        <Textarea
                          value={selectedLesson.content || ''}
                          onChange={(e) => updateLessonContent('content', e.target.value)}
                          placeholder="Description de l'image..."
                          rows={2}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* AUDIO LESSON */}
                  {selectedLesson.lesson_type === 'audio' && (
                    <div className="space-y-4">
                      {selectedLesson.document_url ? (
                        <div className="bg-slate-100 rounded-lg p-4">
                          <audio 
                            src={resolveFileUrl(selectedLesson.document_url)} 
                            controls 
                            className="w-full"
                          />
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                          <input
                            type="file"
                            accept="audio/*"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="audio-upload"
                          />
                          <label htmlFor="audio-upload" className="cursor-pointer">
                            <Music className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                            <p className="text-sm text-slate-600">
                              Cliquez pour uploader un fichier audio
                            </p>
                            <p className="text-xs text-slate-400">MP3, WAV, OGG</p>
                          </label>
                        </div>
                      )}
                      
                      <div>
                        <Label className="mb-2 block">Transcription (optionnelle)</Label>
                        <Textarea
                          value={selectedLesson.content || ''}
                          onChange={(e) => updateLessonContent('content', e.target.value)}
                          placeholder="Transcription du fichier audio..."
                          rows={6}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* AI Quiz Generator */}
              {selectedLesson.lesson_type === 'text' && selectedLesson.content && (
                <Card className="mt-6 border-violet-200 bg-gradient-to-r from-violet-50 to-purple-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                          <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-violet-900">Générateur de Quiz IA</p>
                          <p className="text-sm text-violet-600">
                            Générez automatiquement des questions à partir du contenu
                          </p>
                        </div>
                      </div>
                      <Button 
                        className="bg-violet-600 hover:bg-violet-700"
                        onClick={handleGenerateAIQuiz}
                        disabled={aiGenerating}
                        data-testid="ai-quiz-generate-btn"
                      >
                        {aiGenerating ? (
                          <>
                            <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                            Génération...
                          </>
                        ) : (
                          <>
                            <Brain className="w-4 h-4 mr-2" />
                            Générer un quiz
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-600 mb-2">
                  Sélectionnez une leçon
                </h3>
                <p className="text-sm text-slate-400 mb-4">
                  Choisissez une leçon dans la structure à gauche pour la modifier
                </p>
                {course.modules?.length === 0 && (
                  <Button 
                    onClick={() => setShowModuleDialog(true)}
                    className="bg-violet-600 hover:bg-violet-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Créer votre premier module
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN - Settings Panel */}
        {selectedLesson && (
          <div className="w-72 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 overflow-auto">
            <div className="p-4">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-4">
                Paramètres de la leçon
              </h3>
              
              {/* Duration */}
              <div className="mb-4">
                <Label className="text-sm text-slate-600 mb-1 block">Durée estimée (minutes)</Label>
                <Input
                  type="number"
                  min="1"
                  value={selectedLesson.duration_minutes || ''}
                  onChange={(e) => updateLessonContent('duration_minutes', parseInt(e.target.value) || null)}
                  placeholder="Ex: 15"
                />
              </div>
              
              {/* Visibility */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-slate-600">Visible aux étudiants</Label>
                  <Switch
                    checked={selectedLesson.is_published}
                    onCheckedChange={(checked) => toggleLessonPublish(selectedLesson.id, !checked)}
                  />
                </div>
              </div>
              
              <Separator className="my-4" />
              
              {/* Module info */}
              <div className="mb-4">
                <Label className="text-sm text-slate-600 mb-1 block">Module</Label>
                <p className="font-medium text-slate-900 dark:text-white">
                  {selectedLesson.module?.name}
                </p>
              </div>
              
              {/* Lesson Type */}
              <div className="mb-4">
                <Label className="text-sm text-slate-600 mb-1 block">Type de contenu</Label>
                <Select 
                  value={selectedLesson.lesson_type} 
                  onValueChange={(v) => updateLessonContent('lesson_type', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(lessonTypeLabels).map(([key, label]) => {
                      const Icon = lessonTypeIcons[key];
                      return (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              
              <Separator className="my-4" />
              
              {/* Resources */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm text-slate-600">Ressources jointes</Label>
                  <Button variant="ghost" size="sm">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="text-center py-4 text-slate-400 text-sm border border-dashed rounded-lg">
                  Aucune ressource
                </div>
              </div>
              
              {editorMode === 'advanced' && (
                <>
                  <Separator className="my-4" />
                  
                  {/* Advanced Settings */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-slate-900 dark:text-white text-sm">
                      Paramètres avancés
                    </h4>
                    
                    <div className="flex items-center justify-between">
                      <Label className="text-sm text-slate-600">Obligatoire</Label>
                      <Switch />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label className="text-sm text-slate-600">Quiz requis</Label>
                      <Switch />
                    </div>
                    
                    <div>
                      <Label className="text-sm text-slate-600 mb-1 block">Score minimum</Label>
                      <Input type="number" min="0" max="100" placeholder="70%" />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Module Dialog */}
      <Dialog open={showModuleDialog} onOpenChange={setShowModuleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingModule ? 'Modifier le module' : 'Nouveau module'}
            </DialogTitle>
            <DialogDescription>
              Les modules organisent votre cours en sections thématiques
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nom du module *</Label>
              <Input
                value={moduleForm.name}
                onChange={(e) => setModuleForm({ ...moduleForm, name: e.target.value })}
                placeholder="Ex: Introduction au sujet"
                data-testid="module-name-input"
              />
            </div>
            <div>
              <Label>Description (optionnelle)</Label>
              <Textarea
                value={moduleForm.description}
                onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                placeholder="Décrivez le contenu de ce module..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModuleDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={editingModule ? handleUpdateModule : handleCreateModule}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="submit-module-btn"
            >
              {editingModule ? 'Enregistrer' : 'Créer le module'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lesson Dialog */}
      <Dialog open={showLessonDialog} onOpenChange={setShowLessonDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingLesson ? 'Modifier la leçon' : 'Nouvelle leçon'}
            </DialogTitle>
            <DialogDescription>
              {selectedModule && `Dans le module: ${selectedModule.name}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nom de la leçon *</Label>
              <Input
                value={lessonForm.name}
                onChange={(e) => setLessonForm({ ...lessonForm, name: e.target.value })}
                placeholder="Ex: Comprendre les bases"
                data-testid="lesson-name-input"
              />
            </div>
            
            <div>
              <Label>Type de contenu</Label>
              <Select 
                value={lessonForm.lesson_type} 
                onValueChange={(v) => setLessonForm({ ...lessonForm, lesson_type: v })}
              >
                <SelectTrigger data-testid="lesson-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(lessonTypeLabels).map(([key, label]) => {
                    const Icon = lessonTypeIcons[key];
                    return (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Durée estimée (minutes) (optionnelle)</Label>
              <Input
                type="number"
                min="1"
                value={lessonForm.duration_minutes}
                onChange={(e) => setLessonForm({ ...lessonForm, duration_minutes: e.target.value })}
                placeholder="Ex: 15"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLessonDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={editingLesson ? handleUpdateLesson : handleCreateLesson}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="submit-lesson-btn"
            >
              {editingLesson ? 'Enregistrer' : 'Créer la leçon'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer {deleteTarget?.type === 'module' ? 'le module' : 'la leçon'} "{deleteTarget?.name}" ?
              {deleteTarget?.type === 'module' && ' Toutes les leçons de ce module seront également supprimées.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annuler
            </Button>
            <Button 
              variant="destructive"
              onClick={deleteTarget?.type === 'module' ? handleDeleteModule : handleDeleteLesson}
              data-testid="confirm-delete-btn"
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Course Settings Dialog */}
      <Dialog open={showCourseSettings} onOpenChange={setShowCourseSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Paramètres du cours</DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="general">
            <TabsList className="grid grid-cols-3 mb-4">
              <TabsTrigger value="general">Général</TabsTrigger>
              <TabsTrigger value="progression">Progression</TabsTrigger>
              <TabsTrigger value="access">Accès</TabsTrigger>
            </TabsList>
            
            <TabsContent value="general" className="space-y-4">
              <div>
                <Label>Nom du cours</Label>
                <Input value={course.name} readOnly className="bg-slate-50" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={course.description || ''} readOnly className="bg-slate-50" rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Niveau</Label>
                  <Select
                    value={course.level || ''}
                    onValueChange={(v) => updateCourseField('level', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Non défini" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEVEL_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Catégorie</Label>
                  <Input value={course.category || '-'} readOnly className="bg-slate-50" />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="progression" className="space-y-4">
              <div>
                <Label>Mode de progression</Label>
                <Select defaultValue="free">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Libre - Accès à tout le contenu</SelectItem>
                    <SelectItem value="sequential">Séquentiel - Ordre imposé</SelectItem>
                    <SelectItem value="locked">Verrouillé - Déblocage progressif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Quiz requis pour débloquer</Label>
                  <p className="text-xs text-slate-500">Module suivant verrouillé jusqu'à réussite du quiz</p>
                </div>
                <Switch />
              </div>
              
              <div>
                <Label>Score minimum requis (%)</Label>
                <Input type="number" min="0" max="100" defaultValue="70" />
              </div>
            </TabsContent>
            
            <TabsContent value="access" className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Cours publié</Label>
                  <p className="text-xs text-slate-500">Visible pour les étudiants</p>
                  {!course.is_published && !canPublishCourse && (
                    <p className="text-xs text-amber-600 mt-1">
                      Requis pour publier : {getPublishBlockers().join(', ')}.
                    </p>
                  )}
                </div>
                <Switch
                  checked={course.is_published}
                  onCheckedChange={toggleCoursePublish}
                  disabled={!course.is_published && !canPublishCourse}
                />
              </div>
              
              <div>
                <Label>Formateur assigné</Label>
                <Input
                  value={
                    course.trainer_name
                      || (course.has_assigned_trainer ? 'Formateur(s) assigné(s)' : 'Non assigné')
                  }
                  readOnly
                  className="bg-slate-50"
                />
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCourseSettings(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generated Quiz Dialog */}
      <Dialog open={showAIQuizDialog} onOpenChange={setShowAIQuizDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-500" />
              Questions générées par IA
            </DialogTitle>
            <DialogDescription>
              {generatedQuestions.length} questions générées à partir du contenu de la leçon. 
              Vous pouvez les modifier avant de les enregistrer.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {generatedQuestions.map((question, index) => (
              <Card key={index} className="border-l-4 border-l-violet-500">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center font-semibold text-violet-700">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <Badge className="mb-2" variant={
                        question.question_type === 'multiple_choice' ? 'default' :
                        question.question_type === 'true_false' ? 'secondary' : 'outline'
                      }>
                        {question.question_type === 'multiple_choice' ? 'QCM' :
                         question.question_type === 'true_false' ? 'Vrai/Faux' : 'Question ouverte'}
                      </Badge>
                      <p className="font-medium text-slate-900 mb-2">{question.question_text}</p>
                      
                      {question.options && (
                        <div className="space-y-1 mb-2">
                          {question.options.map((option, optIndex) => (
                            <div 
                              key={optIndex} 
                              className={`p-2 rounded text-sm ${
                                option === question.correct_answer 
                                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                  : 'bg-slate-50 text-slate-700'
                              }`}
                            >
                              {option === question.correct_answer && (
                                <CheckCircle className="w-4 h-4 inline mr-1 text-emerald-600" />
                              )}
                              {option}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {question.explanation && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-blue-700">
                          <strong>Explication:</strong> {question.explanation}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAIQuizDialog(false)}>
              Annuler
            </Button>
            <Button 
              className="bg-violet-600 hover:bg-violet-700"
              onClick={() => {
                // TODO: Save questions to quiz
                toast.success('Quiz enregistré (fonctionnalité en cours de développement)');
                setShowAIQuizDialog(false);
              }}
            >
              <Save className="w-4 h-4 mr-2" />
              Enregistrer le quiz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseEditorPage;
